// Admin · Audit log view — who did what when.
// Reads /admin/audit, every page of the chosen window (`adminAudit`).
// Retention is 90 days: batch-worker's `audit_gc` deletes older rows daily.
//
// Field renames vs. MOCK.AUDIT_EVENTS:
//   e.ts            (epoch ms)  → e.ts (ISO string)
//   e.diff          (object)    → e.diff (AuditDiff[] | null) — first entry rendered inline,
//                                  full list shown in drawer
//   e.target.type   → e.target.kind
//   e.actor.you     → derived from useMe() (highlight when actor.id === me.id;
//                     `sessionMe` already unwraps the `{ user }` envelope)

import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useMemo, useState } from 'react';
import { Icons } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { useAuditEvents, type AuditQuery } from '@/api/hooks';
import type { AuditEvent } from '@/api/types';
import { useMe } from '@/auth/useMe';
import { fmtClock, fmtStampEt, fmtDate, fmtDateShort, nyParts, nyMidnightMs, TZ_LABEL } from '@/lib/datetime';
import { Avatar } from './admin-users';
import { fmtRelative } from './admin-user-drawer';
import { PageActions } from '@/components/page-actions';
import { csvFilename, downloadCsv } from '@/lib/csv';
import { auditCsvRows, auditTargetName, auditViaLabel } from '@/lib/audit-export';
import { actionFamilyLabel, auditActionIcon, auditActionTone, auditFamily } from '@/lib/audit-actions';

/** The one retention sentence, on the page and in the drawer. */
const AUDIT_RETENTION_NOTE = 'Entries are kept for 90 days. Export what you need to keep.';

type AuditWindow = NonNullable<AuditQuery['window']>;

const AUDIT_WINDOWS: readonly AuditWindow[] = ['24h', '7d', '30d', '90d'];

function isAuditWindow(id: string): id is AuditWindow {
  return (AUDIT_WINDOWS as readonly string[]).includes(id);
}

type AuditActor = AuditEvent['actor'];

type AuditListRow =
  | { kind: 'day'; label: string; key: string }
  | { kind: 'event'; event: AuditEvent };

interface AuditViewProps {
  /** Supplied by the route's `useSetViewShim`; the audit log navigates nowhere. */
  setView?: (view: string) => void;
}

function AuditView({ setView: _setView }: AuditViewProps) {
  const [q, setQ] = useState('');
  const [actor, setActor] = useState('all');
  const [type, setType] = useState('all');
  const [range, setRange] = useState<AuditWindow>('30d');
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  // Fetch the window the UI advertises. The previous implementation always
  // fetched the backend's 7-day default and only changed a client-side filter,
  // so "30d"/"90d" silently omitted older entries.
  const {
    data: events = [],
    isLoading,
    isError,
    refetch,
  } = useAuditEvents({ window: range, limit: 200 });
  // `sessionMe` strips the `{ user }` envelope inside its request, so `me` IS
  // the user. Reading `me?.user?.id` was always undefined: no "(you)" in the
  // actor filter and no YOU marker on one's own rows.
  const { data: me } = useMe();
  const myId = me?.id;

  const now = Date.now();
  const cutoff = ({
    '24h': now - 86400000,
    '7d':  now - 7 * 86400000,
    '30d': now - 30 * 86400000,
    '90d': now - 90 * 86400000,
  } satisfies Record<AuditWindow, number>)[range];

  const filtered = useMemo(() => {
    let out = events;
    if (cutoff) out = out.filter(e => +new Date(e.ts) >= cutoff);
    if (actor !== 'all') out = out.filter(e => e.actor.id === actor);
    if (type !== 'all')  out = out.filter(e => e.action.startsWith(type + '.'));
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      out = out.filter(e =>
        e.actor.name.toLowerCase().includes(n) ||
        auditTargetName(e).toLowerCase().includes(n) ||
        e.verb.toLowerCase().includes(n)
      );
    }
    return out;
  }, [events, cutoff, actor, type, q]);

  const distinctActors = useMemo(() => {
    const seen = new Map<string, AuditActor>();
    events.forEach(e => { if (!seen.has(e.actor.id)) seen.set(e.actor.id, e.actor); });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  // What the ON filter offers: the action families (`campaign` in
  // `campaign.paused`) the window actually holds, since it matches on that
  // prefix. A fixed list offered Role, Token and Session, which no server
  // action carries, so picking one always showed nothing. The current choice
  // stays listed so the dropdown never loses its selection.
  const distinctTypes = useMemo(() => {
    const families = new Set(events.map(e => auditFamily(e.action)).filter(Boolean));
    if (type !== 'all') families.add(type);
    return [...families].sort();
  }, [events, type]);

  // `filtered` is the whole window after the filters: `adminAudit` follows the
  // cursor to the window's end or fails, so nothing here is a first page.
  const canExport = !isLoading && !isError && filtered.length > 0;
  const exportCsv = () => {
    if (!canExport) return;
    downloadCsv(csvFilename(`audit-log-${range}`), auditCsvRows(filtered));
  };
  const exportLabel = filtered.length === 1
    ? 'Export 1 entry as CSV'
    : `Export ${filtered.length.toLocaleString()} entries as CSV`;

  const grouped = useMemo(() => {
    const out: AuditListRow[] = [];
    let lastDay: string | null = null;
    filtered.forEach(e => {
      const p = nyParts(e.ts);
      const dayKey = `${p.y}-${p.m}-${p.d}`; // NY calendar day
      if (dayKey !== lastDay) {
        out.push({ kind: 'day', label: formatDay(e.ts), key: dayKey });
        lastDay = dayKey;
      }
      out.push({ kind: 'event', event: e });
    });
    return out;
  }, [filtered]);

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Audit log</h1>
        </div>
        <PageActions>
          <button
            className="btn btn-icon-phone"
            data-phone="inline"
            aria-label="Export"
            title={canExport ? exportLabel : 'Nothing to export in this window'}
            disabled={!canExport}
            onClick={exportCsv}
          ><span>{Icons.dl}</span><span className="phone-long">Export</span></button>
        </PageActions>
      </div>

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0', minWidth: 240}}>
          <span style={{color:'var(--ink-3)'}}>{Icons.search}</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by actor, action, or target…"/>
        </div>
        <Dropdown label="WHO" value={actor} options={[
          { id: 'all', label: 'Anyone' },
          ...distinctActors.map(a => ({ id: a.id, label: a.name + (a.id === myId ? ' (you)' : '') })),
        ]} onChange={setActor} width={210}/>
        <Dropdown label="ON" value={type} options={[
          { id: 'all', label: 'Anything' },
          ...distinctTypes.map(t => ({ id: t, label: actionFamilyLabel(t) })),
        ]} onChange={setType} width={170}/>
        <Dropdown label="WHEN" data-filter-default="30d" value={range} options={[
          { id: '24h', label: 'Last 24 hours' },
          { id: '7d',  label: 'Last 7 days' },
          { id: '30d', label: 'Last 30 days' },
          { id: '90d', label: 'Last 90 days · everything' },
        ]} onChange={(id: string) => { if (isAuditWindow(id)) setRange(id); }} width={230} rightAlign/>
      </ResponsiveFilters>

      <div style={{padding:'10px 22px', background:'var(--bg-elev)', borderBottom:'1px solid var(--line)', display:'flex', alignItems:'center', gap:10, fontFamily:'var(--font-mono)', fontSize:10.5, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--ink-3)'}}>
        <span>{filtered.length.toLocaleString()} ENTRIES · 90-DAY RETENTION · {distinctActors.length} ACTORS · TIMES {TZ_LABEL}</span>
        <span style={{flex:1}}/>
        <span data-testid="audit-retention-note">{AUDIT_RETENTION_NOTE}</span>
      </div>

      <div role="region" aria-label="Audit events" tabIndex={0} style={{flex:1, overflow:'auto', padding:'8px 22px 60px'}}>
        {isLoading && grouped.length === 0 && (
          <div className="muted" style={{padding:'40px 12px', fontSize:13, fontStyle:'italic', textAlign:'center'}}>Loading audit log…</div>
        )}
        {isError && (
          <div className="muted" role="alert" style={{padding:'40px 12px', fontSize:13, textAlign:'center'}}>
            Could not load the audit log.{' '}
            <button className="btn btn-sm" onClick={() => refetch()}>Try again</button>
          </div>
        )}
        {!isLoading && !isError && grouped.length === 0 && (
          <div className="muted" style={{padding:'40px 12px', fontSize:13, fontStyle:'italic', textAlign:'center'}}>
            {events.length === 0 ? 'No audit entries yet.' : 'No audit entries match these filters.'}
          </div>
        )}
        {grouped.map((row) => (
          row.kind === 'day' ? (
            <div key={row.key} className="audit-day">
              <div className="audit-day-line"/>
              <div className="audit-day-pill">{row.label}</div>
              <div className="audit-day-line"/>
            </div>
          ) : (
            <AuditRow key={row.event.id} event={row.event} youId={myId} onOpen={() => setSelected(row.event)}/>
          )
        ))}
      </div>

      {selected && <AuditDetailDrawer event={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

// ── Single audit row ───────────────────────────────────────────────────
interface AuditRowProps {
  event: AuditEvent;
  youId: string | undefined;
  onOpen: () => void;
}

function AuditRow({ event, youId, onOpen }: AuditRowProps) {
  const tone = auditActionTone(event);
  const c = tone === 'danger' ? 'var(--bad)'
    : tone === 'neutral' ? 'var(--ink-2)'
    : ACTION_COLOR[auditFamily(event.action)] ?? 'var(--ink-3)';
  const tsMs = +new Date(event.ts);
  // diff is now Array<AuditDiff> | null — render the first change inline; full list in drawer.
  const firstDiff = event.diff && event.diff.length > 0 ? event.diff[0] : null;
  return (
    <div className="audit-row" role="button" tabIndex={0} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onOpen();}}} onClick={onOpen}>
      <div className="audit-row-icon" data-tone={tone} style={{background: 'color-mix(in oklch, ' + c + ' 14%, transparent)', color: c}}>
        {Icons[auditActionIcon(event)]}
      </div>
      <div className="audit-row-body">
        <div className="audit-row-msg">
          <span className="audit-actor">{event.actor.name}</span>
          {event.actor.id === youId && <span className="audit-you">YOU</span>}
          <span className="audit-verb">{event.verb}</span>
          <span className="audit-target">{auditTargetName(event)}</span>
          <AuditViaChip event={event}/>
          {firstDiff && (
            <span className="audit-diff">
              {firstDiff.field}:&nbsp;
              <span className="audit-before">{firstDiff.before}</span>
              &nbsp;→&nbsp;
              <span className="audit-after">{firstDiff.after}</span>
            </span>
          )}
        </div>
      </div>
      <div className="audit-row-meta">
        <div className="audit-row-time">{fmtRelative(tsMs)}</div>
        <div className="audit-row-stamp">{fmtClock(tsMs)}</div>
      </div>
    </div>
  );
}

// ── Credential chip ────────────────────────────────────────────────────
/** "via API key ‘name’" or "via ChatGPT" (the app) on an entry made with a credential; nothing otherwise. */
function AuditViaChip({ event }: { event: AuditEvent }) {
  const label = auditViaLabel(event);
  if (!label) return null;
  return (
    <span className="tag" data-testid="audit-via" style={{color:'var(--ink-1)', borderColor:'color-mix(in oklch, var(--warn) 45%, var(--line))', whiteSpace:'nowrap'}}>
      {Icons.key} {label}
    </span>
  );
}

// ── Audit detail drawer ────────────────────────────────────────────────
interface AuditDetailDrawerProps {
  event: AuditEvent;
  onClose: () => void;
}

function AuditDetailDrawer({ event, onClose }: AuditDetailDrawerProps) {
  const tsMs = +new Date(event.ts);
  const diff = event.diff;
  return (
    <>

      <ResponsiveOverlay label="admin audit details" onClose={onClose} className="drawer" style={{width:680}}>
        <div className="drawer-hd">
          <div style={{flex:1, minWidth:0}}>
            <div className="muted mono" style={{fontSize:10, letterSpacing:'.1em'}}>
              AUDIT ENTRY · {event.action.toUpperCase()}
            </div>
            <h2 style={{fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, letterSpacing:'.01em', textTransform:'uppercase', margin:'4px 0 4px'}}>
              {event.actor.name} {event.verb}
            </h2>
            <div style={{fontSize:13, color:'var(--ink-1)'}}>{auditTargetName(event)}</div>
            {event.via && <div style={{marginTop:6}}><AuditViaChip event={event}/></div>}
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18, padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          {/* Diff block — only if available; renders each entry in the diff array */}
          {diff && diff.length > 0 && (
            <>
              <div className="field-lbl mb-2">{diff.length === 1 ? 'Change' : 'Changes'}</div>
              <div className="panel" style={{padding:12, marginBottom:14, fontFamily:'var(--font-mono)', fontSize:11.5}}>
                {diff.map((dd, di) => (
                  <div key={di} style={{marginBottom: di === diff.length - 1 ? 0 : 12}}>
                    <div className="muted mono" style={{fontSize:10, letterSpacing:'.1em', textTransform:'uppercase', marginBottom:6}}>
                      field: {dd.field}
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'80px 1fr', rowGap:8, columnGap:10}}>
                      <div className="audit-diff-tag" style={{color:'var(--bad)'}}>BEFORE</div>
                      <div className="audit-diff-val before">{dd.before}</div>
                      <div className="audit-diff-tag" style={{color:'var(--good)'}}>AFTER</div>
                      <div className="audit-diff-val after">{dd.after}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="field-lbl mb-2">Entry</div>
          <div className="panel" style={{padding:'4px 12px', marginBottom:14}}>
            {([
              ['ID',           event.id],
              ['Action',       event.action],
              ['Target type',  event.target.kind],
              ['Target',       auditTargetName(event) + ' (' + event.target.id + ')'],
              ['Timestamp',    fmtStampEt(event.ts) + '  ·  ' + fmtRelative(tsMs)],
              ['IP',           event.ip],
              ['User agent',   event.user_agent],
              ...(event.via ? [['Through', auditViaLabel(event).replace(/^via /, '')] satisfies [string, string]] : []),
            ] satisfies [string, string][]).map(([k, v]) => (
              <div key={k} style={{
                display:'grid', gridTemplateColumns:'120px 1fr',
                padding:'8px 0', borderBottom:'1px solid var(--line-2)',
                fontSize:12,
              }}>
                <div className="muted mono" style={{fontSize:10.5, letterSpacing:'.06em', textTransform:'uppercase'}}>{k}</div>
                <div style={{wordBreak:'break-word'}}>{v}</div>
              </div>
            ))}
          </div>

          <div className="field-lbl mb-2">Actor</div>
          <div className="panel" style={{padding:12, marginBottom:14}}>
            <div style={{display:'flex', alignItems:'center', gap:10}}>
              {Avatar && <Avatar user={{...event.actor, role:'admin', status:'active'}} size={36}/>}
              <div>
                <div style={{fontWeight:600}}>{event.actor.name}</div>
                <div className="mono" style={{fontSize:11, color:'var(--ink-2)'}}>{event.actor.email}</div>
              </div>
            </div>
          </div>

          <div className="muted" style={{fontSize:11, fontStyle:'italic'}}>
            Audit entries are immutable. {AUDIT_RETENTION_NOTE}
          </div>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
// The family colour of a non-destructive row. Red is not a family colour: it
// is `auditActionTone`'s, for destructive rows only, and `user.*` rows that
// are not destructive draw in neutral ink.
const ACTION_COLOR: Partial<Record<string, string>> = {
  campaign:   'var(--accent)',
  audience:   'var(--chart-3)',
  creative:   'var(--chart-2)',
  pixel:      'var(--chart-4)',
  venue:      'var(--ink-2)',
  production: 'var(--ink-2)',
  token:      'var(--warn)',
  api_key:    'var(--warn)',
  oauth_grant:'var(--warn)',
  login:      'var(--good)',
};

// `fmtClock` (NY wall clock) is imported from @/lib/datetime.

// Day-group header, computed on the NY calendar (Today/Yesterday relative to the
// NY day, not the viewer's). Weekday dropped — the datetime module renders the
// literal NY date; the relative prefix already carries the "which day" cue.
function formatDay(v: string): string {
  const ep = nyParts(v);
  const tp = nyParts(Date.now());
  const daysAgo = Math.round((nyMidnightMs(tp.y, tp.m, tp.d) - nyMidnightMs(ep.y, ep.m, ep.d)) / 86400000);
  if (daysAgo === 0) return 'Today · ' + fmtDateShort(v);
  if (daysAgo === 1) return 'Yesterday · ' + fmtDateShort(v);
  if (daysAgo <= 7)  return daysAgo + ' days ago · ' + fmtDateShort(v);
  return fmtDate(v);
}

export { AuditView, AuditRow, AuditDetailDrawer };
